import { createSlice } from '@reduxjs/toolkit';
import includes from 'lodash/includes';
import isNil from 'lodash/isNil';

import { FILTERS_CACHE_KEY } from 'shared/consts';
import { LocalStorage } from 'tools';
import { EArbitrageFilters, EGeneralFilters, EProjectFilters, IFilters } from 'types/filters';
import { IFilterValue } from 'types/filters/common';

import {
  addFilterReducer,
  clearFiltersReducer,
  deleteFilterReducer,
  saveFiltersReducer,
  setupInitialFiltersReducer,
  updateFilterReducer,
} from './reducers';
import { getProjectFiltersKey } from './utils';

const LSValue = LocalStorage.get<IFilters | undefined>(FILTERS_CACHE_KEY);

if (!isNil(LSValue)) {
  if (!includes(Object.keys(LSValue), 'sol')) {
    LSValue.sol = {};
  }
  if (!includes(Object.keys(LSValue), 'project')) {
    LSValue.project = {};
  } else if (LSValue.project) {
    const convertedProjectEntries: IFilters['project'] = {};
    Object.entries(LSValue.project).forEach(([projectIdKey, entry]) => {
      if (!entry) return;

      const filters = entry.filters as
        | Record<string, { filters: IFilterValue[]; setted: IFilterValue[] }>
        | IFilterValue[];

      if (Array.isArray(filters)) {
        convertedProjectEntries[projectIdKey] = {
          updated_at: entry.updated_at,
          filters,
          setted: entry.setted as IFilterValue[],
        };
        return;
      }

      Object.entries(filters ?? {}).forEach(([typeKey, data]) => {
        if (!data) return;
        const key = getProjectFiltersKey(Number(projectIdKey), typeKey as EProjectFilters);

        convertedProjectEntries[key] = {
          updated_at: entry.updated_at,
          filters: data.filters ?? [],
          setted: data.setted ?? [],
        };
      });
    });

    LSValue.project = convertedProjectEntries;
  }
}

const initialState: IFilters = LSValue ?? {
  dex: {},
  cex: {},
  sol: {},
  project: {},
  arbitrage: {
    [EArbitrageFilters.balancer_list]: {},
    [EArbitrageFilters.arbitrages_history]: {},
    [EArbitrageFilters.trades_history]: {},
  },
  [EGeneralFilters.wallets]: {},
  [EGeneralFilters.accounts]: {},
};

const filtersSlice = createSlice({
  name: 'filters',
  initialState: initialState,
  reducers: {
    setupInitialFilters: setupInitialFiltersReducer,
    addFilter: addFilterReducer,
    updateFilter: updateFilterReducer,
    deleteFilter: deleteFilterReducer,
    clearFilters: clearFiltersReducer,
    saveFilters: saveFiltersReducer,
  },
});

export const {
  setupInitialFilters,
  addFilter,
  updateFilter,
  deleteFilter,
  clearFilters,
  saveFilters,
} = filtersSlice.actions;

export default filtersSlice.reducer;
